import { ReactNode, useEffect, useState } from "react";
import { AnimateOnScroll } from "../../../components/animate";
import { Button } from "../../../components/buttons/button";
import { Icon } from "../../../components/icon/icon";
import { TextLoader } from "../../../components/loaders/loaders";
import { RenderMarkdown } from "../../../components/typography/renderMarkdown";
import { useWebsiteButton } from "../../../customeHooks/useWebsiteButtons";
import { sendSupportEmail } from "../../../utils/helpers";

type InfoCard = {
  title: string;
  copy: string;
  icon: string;
};

type InfoPageContent = {
  eyebrow: string;
  title: ReactNode;
  copy: string;
  primaryCta?: {
    text: string;
    action: () => void;
    icon?: string;
  };
  secondaryCta?: {
    text: string;
    action: () => void;
    icon?: string;
  };
  cards: InfoCard[];
  supportTitle: string;
  supportCopy: string;
};

const InfoPageShell = ({ content }: { content: InfoPageContent }) => {
  return (
    <div className="relative overflow-hidden py-14 md:py-20">
      <div className="absolute inset-x-0 top-0 h-[520px] bg-[#F8FBFF]" />
      <div className="landing-hero-mesh absolute inset-x-0 top-0 h-[560px]" />
      <div className="landing-hero-lines absolute inset-x-0 top-0 h-[560px] opacity-60" />

      <div className="max-width-website relative">
        <div className="mx-auto flex w-full max-w-[760px] flex-col items-center gap-4 text-center">
          <div className="shadow-button mb-2 inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/60 px-4 py-2 text-sm font-bold text-grey-900 backdrop-blur-xl">
            <Icon svg="sparkle" width={18} height={18} className="text-blue" />
            {content.eyebrow}
          </div>
          <h1 className="website-title text-[42px] md:text-[68px]">
            {content.title}
          </h1>
          <p className="website-title-text max-w-[680px]">{content.copy}</p>

          {(content.primaryCta || content.secondaryCta) && (
            <div className="mt-4 flex flex-col items-center justify-center gap-2.5 md:flex-row">
              {content.primaryCta ? (
                <Button
                  text={content.primaryCta.text}
                  onClick={content.primaryCta.action}
                  type="button"
                  variation="primary"
                  className="glossy-button !min-w-fit !px-6 !py-4"
                  icon={content.primaryCta.icon ?? "arrow-right"}
                  iconFirst={false}
                  iconDimension={22}
                />
              ) : undefined}

              {content.secondaryCta ? (
                <Button
                  text={content.secondaryCta.text}
                  onClick={content.secondaryCta.action}
                  type="button"
                  variation="plain-grey-2"
                  className="shadow-button !min-w-fit !bg-white/60 !px-5 !py-4 backdrop-blur"
                  icon={content.secondaryCta.icon ?? "arrow-right"}
                  iconFirst={false}
                  iconDimension={20}
                />
              ) : undefined}
            </div>
          )}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3">
          {content.cards.map((card, idx) => (
            <AnimateOnScroll
              key={card.title}
              animation="fade-up"
              delay={0.2 + idx * 0.06}
            >
              <div className="landing-premium-card group flex h-full flex-col gap-5 rounded-[24px] border border-white/80 bg-white/60 p-5 shadow-[0px_18px_54px_rgba(9,21,37,0.10)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_24px_70px_rgba(9,21,37,0.14)] md:p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#C1DBF1] bg-[linear-gradient(135deg,#EDF3FB,#FFFFFF)] text-blue shadow-inset-dual">
                  <Icon svg={card.icon} width={22} height={22} />
                </div>
                <div className="flex flex-col gap-2">
                  <h2 className="text-xl font-bold tracking-[-0.01em] text-primary">
                    {card.title}
                  </h2>
                  <p className="text-base font-medium leading-[145%] text-grey-700">
                    {card.copy}
                  </p>
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        <AnimateOnScroll animation="fade-up" delay={0.35} className="mt-12">
          <div className="landing-premium-card relative overflow-hidden rounded-[28px] border border-white/80 bg-white/55 p-5 shadow-[0px_18px_54px_rgba(9,21,37,0.10)] backdrop-blur-xl md:p-8">
            <div className="absolute right-[-80px] top-[-80px] h-56 w-56 rounded-full bg-blue/20 blur-3xl" />
            <div className="relative flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
              <div className="max-w-[680px]">
                <p className="text-base font-bold text-blue">Need a hand?</p>
                <h2 className="mt-2 font-unbounded text-[30px] font-medium leading-[105%] tracking-[-0.04em] text-primary md:text-[42px]">
                  {content.supportTitle}
                </h2>
                <p className="mt-3 text-lg font-medium leading-[145%] text-grey-800">
                  {content.supportCopy}
                </p>
              </div>

              <div className="flex flex-col gap-2.5 sm:flex-row">
                <Button
                  text="Send an email"
                  onClick={sendSupportEmail}
                  type="button"
                  variation="primary"
                  className="glossy-button !min-w-fit !px-6 !py-4"
                  icon="mail"
                  iconDimension={20}
                />
                <Button
                  text="Instagram DM"
                  onClick={() => {
                    window.open("https://ig.me/m/usetikket", "_blank");
                  }}
                  type="button"
                  variation="plain-grey-2"
                  className="shadow-button !min-w-fit !bg-white/60 !px-5 !py-4 backdrop-blur"
                  icon="instagram"
                  iconDimension={20}
                />
              </div>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </div>
  );
};

export const HelpCenterPage = () => {
  const { goToSignup } = useWebsiteButton();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/assets/help_center.md")
      .then((res) => res.text())
      .then((data) => {
        setContent(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load help center markdown:", err);
        setLoading(false);
      });
  }, []);

  return (
    <div className="relative overflow-hidden py-14 md:py-20">
      <div className="absolute inset-x-0 top-0 h-[520px] bg-[#F8FBFF]" />
      <div className="landing-hero-mesh absolute inset-x-0 top-0 h-[560px]" />

      <div className="max-width-website relative">
        <div className="mx-auto flex w-full max-w-[760px] flex-col items-center gap-4 text-center">
          <div className="shadow-button mb-2 inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/60 px-4 py-2 text-sm font-bold text-grey-900 backdrop-blur-xl">
            <Icon svg="sparkle" width={18} height={18} className="text-blue" />
            Help Center
          </div>
          <h1 className="website-title text-[42px] md:text-[68px]">
            Get answers and keep your{" "}
            <span className="landing-hero-blue-text">event moving.</span>
          </h1>
          <p className="website-title-text max-w-[680px]">
            Practical help for creating events, selling tickets, checking in
            guests, and getting paid.
          </p>
          <Button
            text="Create an Event"
            onClick={() => goToSignup()}
            type="button"
            variation="primary"
            className="glossy-button mt-4 !min-w-fit !px-6 !py-4"
            icon="arrow-right"
            iconFirst={false}
            iconDimension={22}
          />
        </div>

        <AnimateOnScroll
          animation="fade-up"
          delay={0.35}
          className="mx-auto mt-12 max-w-[920px]"
        >
          <div className="landing-premium-card rounded-[28px] border border-white/80 bg-white/70 p-5 shadow-[0px_18px_54px_rgba(9,21,37,0.10)] backdrop-blur-xl md:p-8">
            {loading ? (
              <TextLoader count={12} />
            ) : (
              <div className="prose prose-slate max-w-none">
                <RenderMarkdown usePreWrapper={false}>{content}</RenderMarkdown>
              </div>
            )}
          </div>
        </AnimateOnScroll>
      </div>
    </div>
  );
};

export const ContactPage = () => {
  return (
    <InfoPageShell
      content={{
        eyebrow: "Contact",
        title: (
          <>
            Talk to the team behind{" "}
            <span className="landing-hero-blue-text">Tikket.</span>
          </>
        ),
        copy: "Questions about hosting, payments, partnerships, support, or your next event? Reach out and we will help you move quickly.",
        primaryCta: {
          text: "Send an Email",
          action: sendSupportEmail,
          icon: "mail",
        },
        secondaryCta: {
          text: "Instagram DM",
          action: () => window.open("https://ig.me/m/usetikket", "_blank"),
          icon: "instagram",
        },
        cards: [
          {
            title: "Organizer support",
            copy: "Get help publishing events, setting ticket prices, tracking sales, and scanning guests.",
            icon: "users",
          },
          {
            title: "Attendee support",
            copy: "Questions about a ticket, event details, or entry at the door can be routed to support.",
            icon: "circle-help",
          },
          {
            title: "Partnerships",
            copy: "For venues, communities, brands, and promoters who want to grow with Tikket.",
            icon: "hand-platter",
          },
        ],
        supportTitle: "The fastest way to reach us.",
        supportCopy:
          "Instagram is best for quick questions. Email is best for account, payment, or event-specific support.",
      }}
    />
  );
};

export const AboutPage = () => {
  const { goToFindEvents } = useWebsiteButton();

  return (
    <InfoPageShell
      content={{
        eyebrow: "About Tikket",
        title: (
          <>
            Where events happen and{" "}
            <span className="landing-hero-blue-text">hosts get paid.</span>
          </>
        ),
        copy: "Tikket helps organizers create beautiful event pages, sell tickets, manage attendees, and receive payouts without the usual delays.",
        primaryCta: {
          text: "Find Events",
          action: goToFindEvents,
        },
        cards: [
          {
            title: "Built for real communities",
            copy: "From intimate meetups to culture nights and festivals, Tikket is designed for events people actually show up for.",
            icon: "sparkle",
          },
          {
            title: "Simple for organizers",
            copy: "Create the page, share the link, track sales, validate tickets, and keep the door moving.",
            icon: "calendar",
          },
          {
            title: "Clear from the start",
            copy: "Transparent pricing, no fees on free tickets, and payout flows built around organizer cash flow.",
            icon: "shield",
          },
        ],
        supportTitle: "Want to build with us?",
        supportCopy:
          "We are always interested in hearing from organizers, communities, and partners shaping the future of events.",
      }}
    />
  );
};

export const CareersPage = () => {
  return (
    <InfoPageShell
      content={{
        eyebrow: "Careers",
        title: (
          <>
            Help build the platform for{" "}
            <span className="landing-hero-blue-text">the next event.</span>
          </>
        ),
        copy: "We are building Tikket for organizers and attendees who care about speed, trust, and beautiful event experiences.",
        primaryCta: {
          text: "Contact Us",
          action: sendSupportEmail,
          icon: "mail",
        },
        cards: [
          {
            title: "Product-minded builders",
            copy: "We care about thoughtful systems, polished details, and tools that make hosting feel lighter.",
            icon: "briefcase",
          },
          {
            title: "Close to the community",
            copy: "The best ideas come from listening to organizers, attendees, and the people running the door.",
            icon: "users",
          },
          {
            title: "High-trust execution",
            copy: "Events move quickly. We value people who communicate clearly and ship work that holds up.",
            icon: "shield",
          },
        ],
        supportTitle: "Open roles are coming soon.",
        supportCopy:
          "If you think you can help Tikket grow, send us a short note with what you are excited to work on.",
      }}
    />
  );
};
